/**
 * Pedidos — réplica visual del backoffice de Skeleton:
 *   - 4 KPI cards (Total, Pendientes, En Proceso, Entregadas)
 *   - 2 rows de filtros (status orden + status pago) + búsqueda
 *   - Lista con: número orden / cliente+teléfono / status orden | status pago | email | precio + items + fecha + chevron
 *
 * Se conecta a /api/v1/orders (que devuelve { data: [orders], stats })
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Card, Badge, Input, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  Box, Clock, Truck, CheckCircle2, RefreshCw, Phone, ChevronRight, Mail, Search, ShoppingCart,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/orders/')({
  component: StoreOrdersPage,
})

interface OrderRow {
  id: string
  orderNumber: string
  customerId: string | null
  guestEmail: string | null
  guestName: string | null
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded'
  total: number
  currency: string
  shippingAddress: { firstName?: string; lastName?: string; phone?: string } & Record<string, unknown>
  confirmationEmailSentAt: string | null
  createdAt: string
}

interface OrdersResponse {
  data: OrderRow[]
  stats: {
    total: number
    pending: number
    processing: number
    shipped: number
    delivered: number
    cancelled: number
  }
}

type StatusFilter = 'all' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
type PaymentFilter = 'all' | 'pending' | 'paid' | 'failed' | 'refunded'

function StoreOrdersPage() {
  const { id } = Route.useParams()
  const { activeTenantId } = useAuth()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>('all')
  const [search, setSearch] = useState('')

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['orders', id],
    queryFn: () => tenantApi.get<OrdersResponse>('/api/v1/orders?limit=200'),
    enabled: !!activeTenantId,
  })

  const all = data?.data ?? []
  const stats = data?.stats ?? { total: 0, pending: 0, processing: 0, shipped: 0, delivered: 0, cancelled: 0 }

  const filtered = useMemo(() => {
    let list = all
    if (statusFilter !== 'all') list = list.filter((o) => o.status === statusFilter)
    if (paymentFilter !== 'all') list = list.filter((o) => o.paymentStatus === paymentFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((o) => {
        const customerName = `${o.shippingAddress.firstName ?? ''} ${o.shippingAddress.lastName ?? ''}`.toLowerCase()
        const guestName = (o.guestName ?? '').toLowerCase()
        return o.orderNumber.toLowerCase().includes(q) || customerName.includes(q) || guestName.includes(q)
      })
    }
    return list
  }, [all, statusFilter, paymentFilter, search])

  return (
    <>
      <PageHeader
        title="Pedidos"
        description="Órdenes de los clientes"
        action={
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={cn('size-4 mr-2', isRefetching && 'animate-spin')} /> Refrescar
          </Button>
        }
      />

      <div className="p-8 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Total Órdenes" value={stats.total} icon={Box} color="text-blue-400" />
          <KpiCard label="Pendientes" value={stats.pending} icon={Clock} color="text-yellow-400" />
          <KpiCard label="En Proceso" value={stats.processing} icon={Truck} color="text-orange-400" />
          <KpiCard label="Entregadas" value={stats.delivered} icon={CheckCircle2} color="text-emerald-400" />
        </div>

        {/* Filtros: status orden */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-500 uppercase tracking-wider mr-1">Orden:</span>
          {(['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'] as const).map((s) => (
            <FilterPill key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {s === 'all' ? 'Todas' : s === 'pending' ? 'Pendientes' : s === 'processing' ? 'En Proceso' : s === 'shipped' ? 'Enviadas' : s === 'delivered' ? 'Entregadas' : 'Canceladas'}
            </FilterPill>
          ))}
        </div>

        {/* Filtros: status pago + búsqueda */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-500 uppercase tracking-wider mr-1">Pago:</span>
          {(['all', 'pending', 'paid', 'failed', 'refunded'] as const).map((s) => (
            <FilterPill key={s} active={paymentFilter === s} onClick={() => setPaymentFilter(s)}>
              {s === 'all' ? 'Todos' : s === 'pending' ? 'Pendiente' : s === 'paid' ? 'Pagado' : s === 'failed' ? 'Fallido' : 'Reembolsado'}
            </FilterPill>
          ))}
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-500" />
            <Input
              type="search"
              placeholder="Buscar orden..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>

        {/* Lista de órdenes */}
        {isLoading && <p className="text-sm text-neutral-500">Cargando órdenes...</p>}
        {!isLoading && filtered.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <ShoppingCart className="size-12 text-neutral-700 mb-3" />
            <p className="text-base font-medium">Sin pedidos {all.length > 0 ? 'con esos filtros' : 'todavía'}</p>
            <p className="text-sm text-neutral-500 mt-1">
              {all.length > 0
                ? 'Quita o cambia los filtros para ver más resultados.'
                : 'Cuando los clientes hagan checkout, las órdenes aparecerán aquí.'}
            </p>
          </Card>
        )}

        {filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((o) => (
              <OrderRowCard key={o.id} order={o} tenantId={id} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function KpiCard({ label, value, icon: Icon, color }: {
  label: string
  value: number
  icon: React.ComponentType<{ className?: string }>
  color: string
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs text-neutral-400 uppercase tracking-wider">{label}</p>
        <Icon className={cn('size-4', color)} />
      </div>
      <p className="text-3xl font-bold tabular-nums">{value}</p>
    </Card>
  )
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-sm rounded-md transition border',
        active
          ? 'bg-neutral-800 text-white border-neutral-700'
          : 'bg-neutral-900 text-neutral-400 border-transparent hover:text-white hover:border-neutral-800',
      )}
    >
      {children}
    </button>
  )
}

function OrderRowCard({ order, tenantId }: { order: OrderRow; tenantId: string }) {
  const customerName =
    order.guestName ??
    [order.shippingAddress.firstName, order.shippingAddress.lastName].filter(Boolean).join(' ') ??
    'Cliente'
  const phone = order.shippingAddress.phone ?? ''
  const date = new Date(order.createdAt).toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).replace(',', ' ')

  const statusLabel = (() => {
    switch (order.status) {
      case 'pending': return { label: 'Pendiente', color: 'text-yellow-400' }
      case 'processing': return { label: 'En Proceso', color: 'text-orange-400' }
      case 'shipped': return { label: 'Enviada', color: 'text-blue-400' }
      case 'delivered': return { label: 'Entregada', color: 'text-emerald-400' }
      case 'cancelled': return { label: 'Cancelada', color: 'text-red-400' }
    }
  })()

  const paymentLabel = (() => {
    switch (order.paymentStatus) {
      case 'paid': return { label: 'Pagado', color: 'text-emerald-400' }
      case 'pending': return { label: 'Pendiente', color: 'text-yellow-400' }
      case 'failed': return { label: 'Fallido', color: 'text-red-400' }
      case 'refunded': return { label: 'Reembolsado', color: 'text-neutral-400' }
    }
  })()

  return (
    <Link
      to="/store/$id/orders/$orderId"
      params={{ id: tenantId, orderId: order.id }}
      className="block group"
    >
      <Card className="px-5 py-4 hover:border-neutral-700 transition flex items-center gap-4">
        {/* Order info */}
        <div className="flex-1 min-w-0">
          <p className="font-mono text-sm font-bold tracking-tight">{order.orderNumber}</p>
          <p className="text-sm text-neutral-300 mt-0.5 truncate">{customerName}</p>
          {phone && <p className="text-xs text-neutral-500 mt-0.5 font-mono">{phone}</p>}
        </div>

        {/* Status pills */}
        <div className="hidden md:flex items-center gap-4 text-sm">
          <span className={cn('font-medium', statusLabel.color)}>{statusLabel.label}</span>
          <span className="text-neutral-700">|</span>
          <span className={cn('font-medium', paymentLabel.color)}>{paymentLabel.label}</span>
          {order.confirmationEmailSentAt && (
            <>
              <span className="text-neutral-700">|</span>
              <span className="flex items-center gap-1 text-neutral-400 text-xs">
                <Mail className="size-3.5" /> Email enviado
              </span>
            </>
          )}
        </div>

        {/* Price + date */}
        <div className="text-right shrink-0">
          <p className="font-mono font-bold text-base">{formatMoney(order.total, order.currency)}</p>
          <p className="text-xs text-neutral-500 mt-0.5">{date}</p>
        </div>

        <ChevronRight className="size-5 text-neutral-600 group-hover:text-neutral-300 transition shrink-0" />
      </Card>
    </Link>
  )
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
